import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { BookmarkIcon } from './ui/icons/BookmarkIcon';

export interface WineRowProps {
  wine: {
    _id: string;
    name: string;
    vintage?: number;
    varietal?: string;
    appellation?: string;
    price?: number;
    venueId?: string | { _id: string; name: string } | null;
    proAvg?: number | null;
    proCount?: number;
    memberAvg?: number | null;
    memberCount?: number;
  };
  /** Hide the winery name in the sub-line. Use when the wine is rendered inside a winery group (catalog page, etc.). */
  hideWineryName?: boolean;
}

export const WineRow = ({ wine, hideWineryName = false }: WineRowProps) => {
  const { user } = useAuth();
  const location = useLocation();
  const utils = trpc.useUtils();
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  const venueObj =
    typeof wine.venueId === 'object' && wine.venueId !== null ? wine.venueId : null;
  const wineryName = hideWineryName ? null : venueObj?.name ?? null;

  const isWishlisted = trpc.user.isFavoriteWine.useQuery(
    { wineId: wine._id },
    { enabled: !!user }
  );

  const toggleWishlist = trpc.user.toggleFavoriteWine.useMutation({
    onMutate: () => {
      setOptimistic((prev) => {
        const current = prev ?? isWishlisted.data?.isFavorited ?? false;
        return !current;
      });
    },
    onSuccess: () => {
      setOptimistic(null);
      utils.user.isFavoriteWine.invalidate({ wineId: wine._id });
    },
    onError: () => {
      setOptimistic(null);
    },
  });

  const saved = optimistic ?? isWishlisted.data?.isFavorited ?? false;

  const subParts = [wineryName, wine.varietal, wine.appellation].filter((s): s is string => !!s);
  const subLine = subParts.join(' · ');

  const { proAvg = null, proCount = 0, memberAvg = null, memberCount = 0 } = wine;
  const proLabel =
    proAvg !== null && proCount > 0
      ? `Professional Reviews: ${proAvg} (${proCount} ${proCount === 1 ? 'review' : 'reviews'})`
      : null;
  const memberLabel =
    memberAvg !== null && memberCount > 0
      ? `Member Reviews: ${memberAvg} (${memberCount} ${memberCount === 1 ? 'review' : 'reviews'})`
      : null;

  return (
    <div className="flex items-center gap-4 px-4 py-3.5 bg-gray-50 hover:bg-gray-100 border-b border-gray-200 transition-colors">
      <span className="text-gray-600 text-base min-w-[44px] flex-shrink-0">
        {wine.vintage ?? '—'}
      </span>
      <Link
        to={`/wines/${wine._id}`}
        state={{ from: location.pathname }}
        className="flex-1 min-w-0 group"
      >
        <div className="font-serif text-lg leading-tight text-gray-900 truncate group-hover:text-primary-700">
          {wine.name}
        </div>
        {subLine && (
          <div className="text-sm text-gray-600 mt-0.5 truncate">{subLine}</div>
        )}
        {proLabel && (
          <div className="text-sm text-gray-600 mt-0.5 truncate">{proLabel}</div>
        )}
        {memberLabel && (
          <div className="text-sm text-gray-600 mt-0.5 truncate">{memberLabel}</div>
        )}
      </Link>
      {user && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (toggleWishlist.isPending) return;
            toggleWishlist.mutate({ wineId: wine._id });
          }}
          disabled={toggleWishlist.isPending}
          aria-label={saved ? 'Remove from wishlist' : 'Add to wishlist'}
          className={`inline-flex items-center justify-center w-9 h-9 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-info-500 ${
            saved ? 'text-primary-700' : 'text-gray-700 hover:text-primary-700'
          }`}
        >
          <BookmarkIcon className="h-5 w-5" filled={saved} />
        </button>
      )}
      <span className="text-gray-700 text-base min-w-[60px] text-right flex-shrink-0">
        {wine.price !== undefined ? `$${Math.round(wine.price)}` : ''}
      </span>
    </div>
  );
};
